// The guided tour of My Overtime (formerly Time & Allowances), one of the My Info pages.
//
// Overtime, standby and call-out claims are moving off Zoho People and onto
// this page, so the tour has to carry what Zoho used to: where a claim is made,
// what it needs, and where it goes. Steps point at [data-tour] anchors on
// people/MyInfoPage.jsx. The Log form is a modal: the step inside it opens
// it and the steps either side close it. The allowance type picker and the
// suggestions panel only render in the right conditions, so their bodies read
// standalone.
//
// Wording note: no step mentions a rate or a payment. Rates live on the finance
// Overtime page and nowhere else; here a claim is approved by a line manager
// and the Overtime Policy decides what it pays.

export const MY_ALLOWANCES_TOUR_ID = 'my-allowances';

const openLogModal = () => {
  if (document.querySelector('[data-tour="ot-modal"]')) return;
  document.querySelector('[data-tour="ot-log"]')?.click();
};
const closeLogModal = () => {
  document.querySelector('[data-tour="ot-modal-close"]')?.click();
};

export const myAllowancesTourSteps = [
  {
    title: 'Welcome to My Overtime',
    before: closeLogModal,
    body: [
      'Overtime, standby, call-outs and the working allowances are claimed from here rather than in Zoho. This tour shows where a claim is made, what it needs, and where it goes.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. It will not pop up again either way.',
    ],
  },
  {
    title: 'Four kinds of claim',
    target: '[data-tour="ot-kinds"]',
    before: closeLogModal,
    body: [
      'Overtime is extra hours worked. Standby is being on call. Call-Out is being called on to work outside your hours. Allowances cover desk moves, remote working and out-of-hours work.',
      'Each has its own list, and an amber count on a tab means claims of that kind are still waiting for a decision.',
    ],
  },
  {
    title: 'Logging a claim',
    target: '[data-tour="ot-log"]',
    before: closeLogModal,
    body: [
      'The Log button opens the form for whichever kind is selected. Your line manager approves the claim, and the Overtime Policy decides what it pays. The status in the table below changes when it is decided.',
      'The next step walks through the form.',
    ],
  },
  {
    title: 'When, and what for',
    target: '[data-tour="ot-form"]',
    before: openLogModal,
    body: [
      'Give the start and the end, and the hours follow from them. Neither can be in the future: a claim is for work already done.',
      'The description is required. Say what the work was, briefly, because that is what your manager reads before deciding. Submit sends it for approval.',
    ],
  },
  {
    title: 'Which allowance',
    target: '[data-tour="ot-allowance-types"]',
    before: closeLogModal,
    body: [
      'On the Allowances tab, pick which allowance you are logging before opening the form: a weekday or weekend desk move, remote working, or out of hours (after 5pm, or a Saturday). The form lets you change your mind.',
    ],
  },
  {
    title: 'Suggested from your time log',
    target: '[data-tour="ot-suggestions"]',
    before: closeLogModal,
    body: [
      'On the Overtime tab, hours you logged on a weekend, a bank holiday or over your daily cap in the last two months are offered here, so overtime you have already recorded once does not need typing in again.',
      'Claiming opens the form with the day filled in; the time log has no clock times, so add when the overtime ran, then submit. "Not overtime" sets a suggestion aside; it can be undone for a week, then drops off the list.',
    ],
  },
  {
    title: 'The position at a glance',
    target: '[data-tour="ot-summary"]',
    before: closeLogModal,
    body: [
      'For the kind selected: how many claims are pending, how many there are in total, and the hours approved so far.',
    ],
  },
  {
    title: 'A month at a time',
    target: '[data-tour="ot-months"]',
    before: closeLogModal,
    body: [
      'Claims are grouped by the month they fell in. A green dot means everything in that month is decided; amber means something is still pending.',
    ],
  },
  {
    title: 'List or calendar',
    target: '[data-tour="ot-view"]',
    before: closeLogModal,
    body: [
      'The month shows as a list in date order, with the weekday beside each date, or as a calendar with each claim on the day it starts, so a run of standby can be checked against the days of the week.',
    ],
  },
  {
    title: 'Your claims',
    target: '[data-tour="ot-requests"]',
    before: closeLogModal,
    body: [
      'Every claim for the month, with its status. View opens the detail, including any note from your manager; in the calendar, select a claim to do the same. A pending claim can be cancelled from its row in the list until it is decided.',
    ],
  },
  {
    title: 'That\'s this page',
    before: closeLogModal,
    body: [
      'Claim as you go rather than at month end, and keep an eye on what is still pending. The "?" beside the page title replays this tour any time.',
    ],
  },
];
